const DEFAULT_PERIODIC_THROTTLE_MS = 60 * 60 * 1000; // 1 hour

type UpdateCheckSource = 'scheduled' | 'online';

const lastUpdateCheck: Record<UpdateCheckSource, number> = {
  scheduled: 0,
  online: 0,
};

let updateCheckInProgress = false;
let periodicUpdateCheckIntervalId: number | null = null;

export async function checkForServiceWorkerUpdate(
  throttleMs = DEFAULT_PERIODIC_THROTTLE_MS
): Promise<ServiceWorkerRegistration | null> {
  return runServiceWorkerUpdateCheck('scheduled', throttleMs);
}

async function checkForOnlineServiceWorkerUpdate(
  throttleMs = DEFAULT_PERIODIC_THROTTLE_MS
): Promise<ServiceWorkerRegistration | null> {
  return runServiceWorkerUpdateCheck('online', throttleMs);
}

async function runServiceWorkerUpdateCheck(
  source: UpdateCheckSource,
  throttleMs: number
): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  const now = Date.now();

  if (updateCheckInProgress || now - lastUpdateCheck[source] < throttleMs) {
    return null;
  }

  updateCheckInProgress = true;
  lastUpdateCheck[source] = now;

  try {
    const registration = await navigator.serviceWorker.getRegistration();

    if (!registration) {
      return null;
    }

    await registration.update();

    return registration;
  } catch {
    return null;
  } finally {
    updateCheckInProgress = false;
  }
}

export function startPeriodicServiceWorkerUpdateChecks(
  throttleMs = DEFAULT_PERIODIC_THROTTLE_MS
): void {
  if (periodicUpdateCheckIntervalId !== null) {
    return;
  }

  void checkForServiceWorkerUpdate(throttleMs);

  periodicUpdateCheckIntervalId = window.setInterval(() => {
    void checkForServiceWorkerUpdate(throttleMs);
  }, throttleMs);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkForServiceWorkerUpdate(throttleMs);
    }
  });

  window.addEventListener('online', () => {
    void checkForOnlineServiceWorkerUpdate(throttleMs);
  });
}

export function stopPeriodicServiceWorkerUpdateChecks(): void {
  if (periodicUpdateCheckIntervalId === null) {
    return;
  }

  window.clearInterval(periodicUpdateCheckIntervalId);
  periodicUpdateCheckIntervalId = null;
}
